import {
  addDoc,
  collection,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../../../firebase";

export type MembershipPlan = {
  id: string;
  gymId?: string;
  name: string;
  slug: string;
  stripePriceId: string;
  tierCode: string;
  interval: "month" | "year";
  status: "active" | "archived";
  appAccess: "none" | "limited" | "full";
  bookingPolicy: {
    canBookClasses: boolean;
    allowedClassTags: string[];
    weeklyBookingLimit: number | null;
  };
  priority: number;
  createdAt?: unknown;
  updatedAt?: unknown;
};

export type MembershipPlanInput = {
  id?: string;
  gymId?: string;
  name: string;
  slug: string;
  stripePriceId: string;
  tierCode: string;
  interval: "month" | "year";
  status: "active" | "archived";
  appAccess: "none" | "limited" | "full";
  canBookClasses: boolean;
  allowedClassTags: string[];
  weeklyBookingLimit: number | null;
  priority: number;
};

const MEMBERSHIP_PLANS = "membershipPlans";

function normalisePlan(docId: string, raw: Record<string, any>): MembershipPlan {
  return {
    id: docId,
    gymId: typeof raw.gymId === "string" ? raw.gymId : "default",
    name: String(raw.name || ""),
    slug: String(raw.slug || docId),
    stripePriceId: String(raw.stripePriceId || ""),
    tierCode: String(raw.tierCode || ""),
    interval: raw.interval === "year" ? "year" : "month",
    status: raw.status === "archived" ? "archived" : "active",
    appAccess: raw.appAccess === "none" || raw.appAccess === "limited" ? raw.appAccess : "full",
    bookingPolicy: {
      canBookClasses: raw.bookingPolicy?.canBookClasses !== false,
      allowedClassTags: Array.isArray(raw.bookingPolicy?.allowedClassTags)
        ? raw.bookingPolicy.allowedClassTags.filter((value: unknown) => typeof value === "string")
        : [],
      weeklyBookingLimit:
        typeof raw.bookingPolicy?.weeklyBookingLimit === "number"
          ? raw.bookingPolicy.weeklyBookingLimit
          : null,
    },
    priority: typeof raw.priority === "number" ? raw.priority : 0,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
}

export async function listMembershipPlans() {
  const snap = await getDocs(collection(db, MEMBERSHIP_PLANS));

  return snap.docs
    .map((planDoc) => normalisePlan(planDoc.id, planDoc.data() as Record<string, any>))
    .sort((a, b) => {
      const priorityDiff = a.priority - b.priority;
      if (priorityDiff !== 0) return priorityDiff;
      return a.name.localeCompare(b.name);
    });
}

export async function saveMembershipPlan(input: MembershipPlanInput) {
  const payload = {
    gymId: input.gymId || "default",
    name: input.name.trim(),
    slug: input.slug.trim(),
    stripePriceId: input.stripePriceId.trim(),
    tierCode: input.tierCode.trim(),
    interval: input.interval,
    status: input.status,
    appAccess: input.appAccess,
    bookingPolicy: {
      canBookClasses: input.canBookClasses,
      allowedClassTags: input.allowedClassTags,
      weeklyBookingLimit: input.weeklyBookingLimit,
    },
    priority: input.priority,
    updatedAt: serverTimestamp(),
  };

  if (input.id) {
    await setDoc(
      doc(db, MEMBERSHIP_PLANS, input.id),
      payload,
      { merge: true }
    );

    return input.id;
  }

  const newDoc = await addDoc(collection(db, MEMBERSHIP_PLANS), {
    ...payload,
    createdAt: serverTimestamp(),
  });

  return newDoc.id;
}
